import { createRouter, createWebHistory } from 'vue-router';
import routes from '@/router/routes';
import { getAccessToken } from '@/utils/token';
// eslint-disable-next-line import/no-cycle
import store from '@/store';
import { createApp } from 'vue';
import ModalClass from '@/utils/modal';
import AppFunctions from '@/utils/AppFunctions';
import middlewareAuth from '@/middleware/auth';
import useEstate from '@/uses/useEstate';
import * as screen from '@/constants/screen';
import {
  BID_COMPLETE_COMMON,
  BID_COMPLETE_IMMEDIATE,
  BID_CONFIRM_COMMON,
  BID_CONFIRM_IMMEDIATE,
  BID_CONFIRM_ONETIME,
} from '@/constants/screen';

const app = createApp();
app.use(ModalClass).use(AppFunctions);
const { dataInput } = useEstate();

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior(to, from, savedPosition) {
    if (to.hash) {
      return {
        el: to.hash,
        // behavior: 'smooth',
      };
    }
    if (savedPosition) {
      return savedPosition;
    }
    return {
      top: 0,
      // behavior: 'smooth',
    };
  },
});

/**
 * Create a new router instance.
 *
 * @return {Router}
 */

/**
 * Global middleware
 *
 * @return {Array}
 */
function globalMiddleware() {
  return [middlewareAuth];
}

/**
 * Each a middleware
 *
 * @return {void}
 */
function nextFactory(context, middleware, index) {
  const subsequentMiddleware = middleware[index];
  if (!subsequentMiddleware) return context.next;

  return (...parameters) => {
    context.next(...parameters);
    const nextMiddleware = nextFactory(context, middleware, index + 1);
    subsequentMiddleware({
      ...context,
      next: nextMiddleware,
    });
  };
}

// eslint-disable-next-line
const confirmBeforeEnter = async (to, from, next) => {
  const isContinueAction = store.getters['modal/isContinueAction'];
  if (!isContinueAction) {
    const isNotShowConfirm = app.config.globalProperties.$checkRouteNotShowConfirmPopup(
      from.meta?.isContinueAction,
      to.meta?.isContinueAction
    );
    if (isNotShowConfirm) {
      return next();
    }

    const result = await app.config.globalProperties.$showConfirm();
    if (result) {
      store.commit('modal/changeContinueActionStatus', true);
      const token = getAccessToken();
      if (to.name === screen.SCREEN_TOP) {
        if (!token) {
          return next();
        }
        return next({ name: screen.SCREEN_HOME });
      }
      return next();
    }

    return next(false);
  }
};

// eslint-disable-next-line
const backFromBiddingComplete = async (to, from, next) => {
  if (
    [BID_CONFIRM_COMMON, BID_CONFIRM_IMMEDIATE, BID_CONFIRM_ONETIME].includes(
      to.name
      // eslint-disable-next-line
    ) &&
    [BID_COMPLETE_COMMON, BID_COMPLETE_IMMEDIATE].includes(from.name)
  ) {
    return next({
      name: 'Estate Detail',
      params: {
        id: dataInput.value?.estate_id,
      },
    });
  }
};

// eslint-disable-next-line
const accessBidding = async (to, next) => {
  if (
    [
      BID_CONFIRM_COMMON,
      BID_CONFIRM_IMMEDIATE,
      BID_CONFIRM_ONETIME,
      BID_COMPLETE_COMMON,
      BID_COMPLETE_IMMEDIATE,
      // eslint-disable-next-line operator-linebreak
    ].includes(to.name) &&
    !dataInput.value.price_input
  ) {
    return next({
      name: 'Estate Detail',
      params: {
        id: dataInput.value?.estate_id,
      },
    });
  }
};

// eslint-disable-next-line
router.beforeEach(async (to, from, next) => {
  document.title = to.meta?.title ?? process.env.VUE_APP_NAME ?? '';
  await confirmBeforeEnter(to, from, next);
  await backFromBiddingComplete(to, from, next);
  await accessBidding(to, next);

  let middleware = null;
  let routeMiddleware = null;

  if (to.meta.middleware) {
    routeMiddleware = Array.isArray(to.meta.middleware) ? to.meta.middleware : [to.meta.middleware];
  }
  middleware = routeMiddleware ? globalMiddleware().concat(routeMiddleware) : globalMiddleware();

  if (middleware.length > 0) {
    const context = {
      to,
      from,
      next,
      store,
    };
    const nextMiddleware = nextFactory(context, middleware, 1);

    return middleware[0]({
      ...context,
      next: nextMiddleware,
    });
  }

  // return next();
});

export default router;
